import React, { useMemo } from "react";

import Decimal from "decimal.js";
import { orderBy, uniq } from "lodash";

import useGetTradeProposalBundle from "@fartherfinance/frontend/api/Investment/hooks/useGetTradeProposalBundle";
import { TradeProposalBundleId } from "@fartherfinance/frontend/api/Investment/types";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import { SecurityId } from "@fartherfinance/frontend/api/Types";

import SecurityCell from "../../Proposals/ProposalDetails/SecurityCell";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import { Cell, Row } from "@src/sharedComponents/Table/Types";

type Columns = "Security" | "Proposal Count" | "Total Buys" | "Total Sells";

interface Props {
  tradeProposalBundleId: TradeProposalBundleId;
}

const SecuritiesTable: React.FC<Props> = (props) => {
  const auth = useAdvisorRequestAuth();

  const bundle = useGetTradeProposalBundle(props.tradeProposalBundleId, auth);

  const allSecIds = useMemo((): SecurityId[] | null => {
    if (bundle.data === undefined) {
      return null;
    }

    // Order them as the server does not guarantee stable ordering of holdings
    const secIds = orderBy(
      bundle.data.tradeProposals
        .flatMap((tradeProposal) => tradeProposal.trades)
        .map((trade) => trade.securityId),
      [(x) => x],
      ["asc"]
    );

    if (secIds.length === 0) {
      return [];
    }

    return uniq(secIds);
  }, [bundle.data]);

  const holdingSecurityRequest = useSecuritiesBySecurityIds(allSecIds, auth);

  const securityDb = holdingSecurityRequest.data;

  const rows = useMemo((): Row<Columns, Cell>[] | undefined => {
    if (bundle.data === undefined || securityDb === undefined) {
      return undefined;
    }

    const trades = bundle.data.tradeProposals.flatMap(
      (tradeProposal) => tradeProposal.trades
    );

    const summedTrades = trades.reduce<
      {
        securityId: SecurityId;
        proposalCount: number;
        totalBuys: Decimal;
        totalSells: Decimal;
      }[]
    >((acc, trade) => {
      const existing = acc.find((x) => x.securityId === trade.securityId);

      if (existing) {
        if (trade.side === "Buy") {
          existing.totalBuys = existing.totalBuys.plus(
            trade.quantity.times(trade.price)
          );
        } else {
          existing.totalSells = existing.totalSells.plus(
            trade.quantity.times(trade.price)
          );
        }
        existing.proposalCount++;
      } else {
        acc.push({
          securityId: trade.securityId,
          proposalCount: 1,
          totalBuys:
            trade.side === "Buy"
              ? trade.quantity.times(trade.price)
              : new Decimal(0),
          totalSells:
            trade.side === "Sell"
              ? trade.quantity.times(trade.price)
              : new Decimal(0),
        });
      }
      return acc;
    }, []);

    return summedTrades.map((trade) => ({
      key: trade.securityId,
      Security: {
        value: <SecurityCell securityId={trade.securityId} />,
        fullValue:
          securityDb
            .find((x) => x.id === trade.securityId)
            ?.identifiers.find((i) => i.registrar === "TICKER")?.identifier ??
          "ZZZZZZ",
      },
      "Proposal Count": trade.proposalCount.toLocaleString("en-US"),
      "Total Buys": trade.totalBuys
        .toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }),
      "Total Sells": trade.totalSells
        .toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }),
    }));
  }, [bundle.data, securityDb]);

  return (
    <>
      {rows === undefined ? (
        <FullDataTable<Columns, Cell>
          key={"details-loading"}
          useQueryParams={false}
          columns={["Security", "Proposal Count", "Total Buys", "Total Sells"]}
          defaultSortColumn={["Security", "asc"]}
          rows={Array.from({ length: 10 }).map((_, idx) => ({
            key: idx.toString(),
            Security: { value: <Skeleton />, fullValue: "" },
            "Proposal Count": { value: <Skeleton />, fullValue: "" },
            "Total Buys": { value: <Skeleton />, fullValue: "" },
            "Total Sells": { value: <Skeleton />, fullValue: "" },
          }))}
          emptyCell={<Skeleton />}
          hidePagination
          defaultRowsPerPage={10}
        />
      ) : (
        <FullDataTable<Columns, Cell>
          key={"details-loaded"}
          useQueryParams={false}
          columns={["Security", "Proposal Count", "Total Buys", "Total Sells"]}
          defaultSortColumn={["Security", "asc"]}
          rows={rows}
          emptyCell={<Skeleton />}
          hidePagination
          defaultRowsPerPage={rows.length}
        />
      )}
    </>
  );
};

export default SecuritiesTable;
